import { Inject, Injectable } from '@angular/core';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { SiteBookingSettings } from '@app/models/siteBookingSettings.model';
import { getObjectDelta, ObjectDelta } from '@app/shared/helpers/object-delta.helper';
import { LOCAL_STORAGE, StorageTranscoders, WebStorageService } from 'ngx-webstorage-service';
import { BehaviorSubject, map, Observable, pairwise } from 'rxjs';

export const CUSTOMER_ID = 'CID';
export const SITE_BOOKING_SETTINGS = 'siteSettings';
export const SITE = 'site';
export const PET_LIST = 'petList';
export const ONLINE_ID = 'onlineID';
export const APPOINTMENT_REQUEST = 'appointmentRequest';
export const OLD_APPOINTMENT_REQUEST = 'oldAppointmentRequest';
@Injectable({
    providedIn: 'root',
})
export class LocalStorageService {
    private _siteBookingSettingsSubject = new BehaviorSubject<SiteBookingSettings | null>(null);
    public localStorageSiteBookingSettings$: Observable<SiteBookingSettings | null> = this._siteBookingSettingsSubject.asObservable();
    private _appointmentRequestSubject!: BehaviorSubject<AppointmentRequest | null>;
    public get appointmentRequestUpdated(): Observable<{
        current: AppointmentRequest | null;
        previous: AppointmentRequest | null;
        delta: ObjectDelta<AppointmentRequest>;
    }> {
        return this._appointmentRequestSubject.asObservable().pipe(
            pairwise(),
            map(([previous, current]) => {
                const delta = getObjectDelta(previous || {}, current || {});
                return { current, previous, delta };
            })
        );
    }

    constructor(@Inject(LOCAL_STORAGE) private storage: WebStorageService) {
        // initialize the appointment request behavior subject from local storage if it's there
        const appointmentRequest = this.storage.get(APPOINTMENT_REQUEST);
        let initialValue: AppointmentRequest | null = null;
        if (appointmentRequest) {
            initialValue = appointmentRequest as AppointmentRequest;
        }
        this._appointmentRequestSubject = new BehaviorSubject<AppointmentRequest | null>(initialValue);
    }

    public get(key: string): object {
        return this.storage.get(key);
    }
    public getString(key: string): string {
        const value = this.storage.get(key, StorageTranscoders.STRING);
        return this.removeDuplicateQuotes(value);
    }

    public clear(keys?: string[]): void {
        if (keys) {
            keys.forEach((val) => this.storage.remove(val));
        } else {
            this.storage.clear();
        }
    }

    public set(key: string, value: any): void {
        this.storage.set(key, value);
        if (key === SITE_BOOKING_SETTINGS) {
            this._siteBookingSettingsSubject.next(value as SiteBookingSettings);
        } else if (key === APPOINTMENT_REQUEST) {
            this._appointmentRequestSubject.next(value as AppointmentRequest);
        }
    }

    public setString(key: string, value: string): void {
        this.storage.set(key, value, StorageTranscoders.STRING);
    }

    public setNested(key: string, nestedKey: string, value: any): void {
        if (this.has(key)) {
            const fullValue = this.get(key) as any;
            fullValue[nestedKey] = value;

            this.set(key, fullValue);
        } else {
            const fullValue = {} as any;
            fullValue[nestedKey] = value;

            this.set(key, fullValue);
        }
    }

    public has(key: string): boolean {
        return this.storage.has(key);
    }

    public remove(key: string): void {
        this.storage.remove(key);
        if (key === SITE_BOOKING_SETTINGS) {
            this._siteBookingSettingsSubject.next(null);
        }
        if (key === APPOINTMENT_REQUEST) {
            this._appointmentRequestSubject.next(null);
        }
    }

    private removeDuplicateQuotes(value: string): string {
        if (value?.startsWith('"')) {
            value = JSON.parse(value);
        }
        return value;
    }

    removeNested(key: string, nestedKey: string): void {
        const data = this.get(key) as Record<string, any>;
        if (data && typeof data === 'object') {
            delete data[nestedKey];
            this.set(key, data);
        }
    }
}
